import { useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, InlineStack, Select, Text } from '@shopify/polaris';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useLanguage } from '../providers/LanguageProvider';
import type { GarmentFit, GarmentNormalization, SizeGuideEntryView, SizeGuidePatch, SizeGuidePreviewResponse } from '../types';

export function GarmentConversion({ entry, busy, onPatch, onRefresh, error }: {
  entry: SizeGuideEntryView; busy: boolean; onPatch: (id: string, patch: SizeGuidePatch) => void; onRefresh?: (id: string) => Promise<void>; error: boolean;
}) {
  const { t, language } = useLanguage();
  const s = t.settings;
  const authFetch = useAuthenticatedFetch();
  const [preview, setPreview] = useState<GarmentNormalization | null>(null);
  const [selectedFit, setSelectedFit] = useState<GarmentFit | null>(entry.conversion_config?.fit_override ?? null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef(0);
  useEffect(() => {
    request.current++;
    setPreview(null); setLoading(false); setFailed(false);
    setSelectedFit(entry.conversion_config?.fit_override ?? null);
  }, [entry.revision, entry.conversion_config?.fit_override]);
  useEffect(() => () => { request.current++; }, []);
  const normalization = preview ?? entry.structuredSizeGuide.normalization;
  const previewFit = async (fit: GarmentFit | null) => {
    const seq = ++request.current;
    setLoading(true); setFailed(false); setPreview(null);
    try {
      const response = await authFetch(`/api/settings/size-guides/${entry.id}/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fit_override: fit, expected_revision: entry.revision ?? 0 }),
      });
      const result: SizeGuidePreviewResponse = await response.json();
      if (response.status === 409 && onRefresh) {
        if (seq === request.current) { setFailed(true); await onRefresh(entry.id); }
        return;
      }
      if (!response.ok || !result.success || !result.data?.structuredSizeGuide.normalization) throw new Error('preview_failed');
      if (seq === request.current) { setPreview(result.data.structuredSizeGuide.normalization); setSelectedFit(fit); }
    } catch { if (seq === request.current) setFailed(true); }
    finally { if (seq === request.current) setLoading(false); }
  };
  const fitLabels = { slim: s.garmentSlim, regular: s.garmentRegular, oversize: s.garmentOversize };
  const ready = normalization?.status === 'ready' && (preview !== null || entry.usable !== false);
  const dimensions = [...new Set(normalization?.effective_rows.flatMap((row) => Object.keys(row.ranges)) ?? [])];
  const labels: Record<string, string | undefined> = s.measurements;
  const number = new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-US', { maximumFractionDigits: 1 });
  return <BlockStack gap="300">
    {ready ? <Banner tone="info" title={s.garmentConverted}><p>{s.garmentExplanation}</p></Banner>
      : entry.structuredSizeGuide.normalization_pending || !normalization || normalization.status === 'ready'
        ? <Banner tone="info"><p>{s.garmentPending}</p></Banner>
        : <Banner tone="critical" title={s.garmentUnusable}><p>{s.garmentUnusableReason}</p></Banner>}
    <Select label={s.garmentFit} value={selectedFit ?? normalization?.resolved_fit ?? 'regular'}
      disabled={busy || loading} options={Object.entries(fitLabels).map(([value, label]) => ({ value, label }))}
      onChange={(value) => { if (value === 'slim' || value === 'regular' || value === 'oversize') void previewFit(value); }} />
    {normalization && <Text as="p" variant="bodySm">{normalization.fit_origin === 'default' ? s.garmentDefault : normalization.fit_origin === 'inferred' ? s.garmentInferred : s.garmentOverride}</Text>}
    <InlineStack gap="200">
      <Button disabled={busy || loading} onClick={() => void previewFit(null)}>{s.garmentReset}</Button>
      {preview && <Button variant="primary" disabled={busy || loading} onClick={() => onPatch(entry.id, { fit_override: selectedFit, expected_revision: entry.revision ?? 0 })}>{s.garmentSave}</Button>}
    </InlineStack>
    <div role="status" aria-live="polite">{loading ? s.garmentPreviewing : preview ? s.garmentPreviewReady : null}</div>
    {error ? <Banner tone="critical"><p>{s.garmentSaveFailure}</p></Banner> : failed && <Banner tone="critical"><p>{s.garmentPending}</p></Banner>}
    {normalization?.fit_suggestion && normalization.resolved_fit !== normalization.fit_suggestion.fit && <Banner tone="info" title={s.garmentSuggestion}>
      <p>{s.garmentSuggestionReason}</p><Button disabled={busy || loading} onClick={() => void previewFit('oversize')}>{s.garmentApplySuggestion}</Button>
    </Banner>}
    {ready && normalization && <BlockStack gap="200">
      <Text as="h3" variant="headingSm">{s.garmentBodyTable}</Text>
      <div style={{ overflowX: 'auto' }} className="ph-no-capture">
        <table style={{ width: '100%', textAlign: 'left' }} aria-label={s.garmentBodyTable}>
          <thead><tr><th scope="col">{s.sizeGuideSizeColumn}</th>{dimensions.map((key) => <th scope="col" key={key}>{labels[key] ?? key}</th>)}</tr></thead>
          <tbody>{normalization.effective_rows.map((row) => <tr key={row.source_index}><th scope="row">{row.size_label}</th>{dimensions.map((key) => {
            const range = row.ranges[key];
            return <td key={key}>{range ? `${number.format(Math.floor(range.support_min * 10) / 10)}${language === 'it' ? ' a ' : ' to '}${number.format(Math.ceil(range.support_max * 10) / 10)}` : '-'}</td>;
          })}</tr>)}</tbody>
        </table>
      </div>
      {normalization.omitted_dimensions.some((d) => d.dimension === 'shoulder_width_cm') && <Text as="p">{s.garmentShoulder}</Text>}
      {normalization.confidence === 'low' && <Text as="p">{s.garmentLowConfidence}</Text>}
    </BlockStack>}
  </BlockStack>;
}
